// Abrechnung und Lastschrifteinzug — Inhaberbereich, bewusst deutsch.
//
// Der Weg von oben nach unten ist der Weg, den der Monat nimmt:
// einrichten, abrechnen, ankündigen, Datei ziehen, Ergebnis eintragen.
// Jeder Schritt sagt, was ihn blockiert — nicht erst die Bank beim Hochladen.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiFehler, token } from '../../../gemeinsam/lib/api';
import { datum, euro } from '../../../gemeinsam/lib/daten';
import { Abzeichen, Auswahl, Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Konfig = {
  betriebId: string | null; name: string; glaeubigerId: string; iban: string; bic: string;
  vorlaufTage: number; ankuendigungTage: number; mandatFristTage: number; steuersatz: number;
  stand: { vollstaendig: boolean; fehlt: string[] };
  betriebe: { id: string; name: string }[];
  versandBereit: boolean;
};
type VorschauZeile = {
  tenantId: string; name: string; plan: string; bezeichnung: string;
  netto: number; brutto: number; hatMandat: boolean;
  schonAbgerechnet: boolean; rechnungsnummer: string | null;
  abrechnen: boolean; gruende: string[];
};
type Vorschau = {
  monat: string; monatName: string; zeilen: VorschauZeile[];
  summe: { anzahl: number; netto: number; brutto: number; ohneMandat: number };
};
type EinzugZeile = {
  rechnungId: string; nummer: string; mandantName: string; brutto: number; status: string;
  hatMandat: boolean; mandatsreferenz: string | null; kontoinhaber: string | null;
  ibanKurz: string | null; angekuendigtAm: string | null;
  einzug: { status: string; faelligAm?: string; folge?: string; grund?: string } | null;
};
type Einzug = {
  monat: string; zeilen: EinzugZeile[];
  einziehbar: { anzahl: number; summe: number; ohneAnkuendigung: number };
  fruehesteFaelligkeit: string; vorlaufTage: number;
};
type MandatZeile = {
  tenantId: string; name: string; plan: string; befreit: boolean; hatMandat: boolean;
  kontoinhaber: string | null; ibanKurz: string | null; mandatsreferenz: string | null;
  unterschriebenAm: string | null; fristBis: string | null; tageRest: number | null; gesperrt: boolean;
  vorEinrichtung: boolean; referenzNachgetragen: boolean; eigenerBetrieb?: boolean;
};

const EINZUG_NAME: Record<string, string> = {
  offen: 'offen', eingereicht: 'bei der Bank', eingezogen: 'eingezogen', zurueck: 'zurückgekommen',
};
const EINZUG_FARBE: Record<string, 'neutral' | 'blau' | 'gruen' | 'rot'> = {
  offen: 'neutral', eingereicht: 'blau', eingezogen: 'gruen', zurueck: 'rot',
};

/** Die letzten Monate zur Auswahl — rückwärts, weil man meist den laufenden will. */
function monate(anzahl = 6): string[] {
  const heute = new Date();
  return Array.from({ length: anzahl }, (_, i) => {
    const d = new Date(Date.UTC(heute.getUTCFullYear(), heute.getUTCMonth() - i, 1));
    return d.toISOString().slice(0, 7);
  });
}

export default function Abrechnung() {
  const [monat, setMonat] = useState(monate()[0]);
  const [konfig, setKonfig] = useState<Konfig | null>(null);
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [einzug, setEinzug] = useState<Einzug | null>(null);
  const [mandate, setMandate] = useState<{ zeilen: MandatZeile[]; ohneMandat: number; gesperrt: number } | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const [k, v, e, m] = await Promise.all([
        api<Konfig>('/abrechnung/konfig'),
        api<Vorschau>(`/abrechnung/vorschau?monat=${monat}`),
        api<Einzug>(`/abrechnung/einzug?monat=${monat}`),
        api<{ zeilen: MandatZeile[]; ohneMandat: number; gesperrt: number }>('/abrechnung/mandate'),
      ]);
      setKonfig(k); setVorschau(v); setEinzug(e); setMandate(m);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Abrechnung ließ sich nicht laden.');
    }
  }, [monat]);

  useEffect(() => { void laden(); }, [laden]);

  async function tun(name: string, fn: () => Promise<string>) {
    setLaeuft(name); setFehler(null); setMeldung(null);
    try {
      setMeldung(await fn());
      await laden();
    } catch (e) {
      if (e instanceof ApiFehler) {
        const maengel = (e.daten?.maengel as { posten: string | null; text: string }[]) || [];
        setFehler(maengel.length
          ? `${e.message}: ${maengel.map(m => (m.posten ? `${m.posten} — ${m.text}` : m.text)).join(' · ')}`
          : e.message);
      } else setFehler('Das hat nicht geklappt.');
    } finally { setLaeuft(null); }
  }

  if (!konfig) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Abrechnung &amp; Einzug</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {vorschau?.monatName} · {mandate?.ohneMandat ?? 0} Mandanten ohne Mandat
          </p>
        </div>
        <div className="w-44">
          <Auswahl label="Monat" value={monat} onChange={e => setMonat(e.target.value)}>
            {monate(12).map(m => <option key={m} value={m}>{m}</option>)}
          </Auswahl>
        </div>
      </div>

      {fehler && <Blickfang ausloeser={fehler}><Hinweis art="fehler">{fehler}</Hinweis></Blickfang>}
      <Erfolgsmeldung text={meldung} />

      {!konfig.stand.vollstaendig && (
        <Hinweis art="warnung" titel="Einzug noch nicht einsatzbereit">
          Es fehlt: {konfig.stand.fehlt.join(', ')}. Ohne diese Angaben weist jede Bank die Datei ab.
          Die Gläubiger-Identifikationsnummer beantragen Sie kostenlos bei der Bundesbank
          (extranet.bundesbank.de), sie kommt in wenigen Werktagen.
        </Hinweis>
      )}

      <Einstellungen konfig={konfig} beiGespeichert={laden} setFehler={setFehler} setMeldung={setMeldung} />

      {/* Abrechnungslauf */}
      <Karte>
        <KartenKopf
          titel="Abrechnungslauf"
          hinweis="Je Mandant eine Rechnung in Ihrem eigenen Betrieb — mit Umsatzsteuer und fortlaufender Nummer."
          aktion={vorschau && vorschau.summe.anzahl > 0 ? (
            <Knopf variante="primaer" laedt={laeuft === 'lauf'}
                   onClick={() => tun('lauf', async () => {
                     const r = await api<{ anzahl: number; brutto: number }>('/abrechnung/lauf',
                       { methode: 'POST', daten: { monat } });
                     return `${r.anzahl} Rechnungen über ${euro(r.brutto)} erstellt.`;
                   })}>
              {vorschau.summe.anzahl} abrechnen · {euro(vorschau.summe.brutto)}
            </Knopf>
          ) : undefined}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr><th className="px-5 py-2">Mandant</th><th className="px-5 py-2">Leistung</th>
                <th className="px-5 py-2 text-right">Netto</th><th className="px-5 py-2 text-right">Brutto</th>
                <th className="px-5 py-2">Stand</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {vorschau?.zeilen.map(z => (
                <tr key={z.tenantId} className={z.abrechnen ? '' : 'opacity-60'}>
                  <td className="px-5 py-2.5">
                    <span className="font-medium text-slate-900 dark:text-slate-100">{z.name}</span>
                    {!z.hatMandat && <Abzeichen art="gelb">kein Mandat</Abzeichen>}
                  </td>
                  <td className="px-5 py-2.5 text-slate-600 dark:text-slate-300">{z.bezeichnung}</td>
                  <td className="px-5 py-2.5 text-right">{euro(z.netto)}</td>
                  <td className="px-5 py-2.5 text-right font-medium">{euro(z.brutto)}</td>
                  <td className="px-5 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                    {z.rechnungsnummer || z.gruende.join(', ') || 'wird abgerechnet'}
                  </td>
                </tr>
              ))}
              {!vorschau?.zeilen.length && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">Keine Mandanten.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Karte>

      {einzug && einzug.zeilen.length > 0 && (
        <EinzugsKarte einzug={einzug} monat={monat} laeuft={laeuft} tun={tun}
                      konfigBereit={konfig.stand.vollstaendig} setFehler={setFehler} />
      )}

      <MandatsKarte mandate={mandate} laeuft={laeuft} tun={tun} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function Einstellungen({ konfig, beiGespeichert, setFehler, setMeldung }: {
  konfig: Konfig; beiGespeichert: () => Promise<void>;
  setFehler: (s: string | null) => void; setMeldung: (s: string | null) => void;
}) {
  const [offen, setOffen] = useState(!konfig.stand.vollstaendig);
  const [f, setF] = useState({
    betriebId: konfig.betriebId || '', name: konfig.name, glaeubigerId: konfig.glaeubigerId,
    iban: konfig.iban, bic: konfig.bic, vorlaufTage: String(konfig.vorlaufTage),
    mandatFristTage: String(konfig.mandatFristTage),
  });
  const [sendet, setSendet] = useState(false);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setSendet(true); setFehler(null); setMeldung(null);
    try {
      await api('/abrechnung/konfig', {
        methode: 'PUT',
        daten: { ...f, vorlaufTage: Number(f.vorlaufTage), mandatFristTage: Number(f.mandatFristTage) },
      });
      setMeldung('Einstellungen gespeichert.');
      await beiGespeichert();
      setOffen(false);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Speichern fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <Karte>
      <KartenKopf
        titel="Wer einzieht"
        hinweis={konfig.stand.vollstaendig
          ? `${konfig.name} · ${konfig.glaeubigerId}`
          : 'Noch nicht vollständig'}
        aktion={<Knopf variante="still" onClick={() => setOffen(o => !o)}>{offen ? 'Zuklappen' : 'Ändern'}</Knopf>}
      />
      {offen && (
        <form onSubmit={speichern} className="grid gap-4 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Auswahl label="Eigener Betrieb — hier entstehen die Rechnungen"
                     value={f.betriebId} onChange={e => setF({ ...f, betriebId: e.target.value })}>
              <option value="">— bitte wählen —</option>
              {konfig.betriebe.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Auswahl>
          </div>
          <Feld label="Name des Einziehenden" value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
                hinweis="So, wie er beim Zahler auf dem Kontoauszug stehen soll." />
          <Feld label="Gläubiger-Identifikationsnummer" value={f.glaeubigerId} className="font-mono"
                hinweis="Von der Bundesbank, Form DE98ZZZ09999999999"
                onChange={e => setF({ ...f, glaeubigerId: e.target.value.toUpperCase() })} />
          <Feld label="Eigene IBAN" value={f.iban} className="font-mono"
                onChange={e => setF({ ...f, iban: e.target.value.toUpperCase() })} />
          <Feld label="Eigener BIC" value={f.bic} className="font-mono"
                onChange={e => setF({ ...f, bic: e.target.value.toUpperCase() })} />
          <Feld label="Vorlauf in Bankarbeitstagen" value={f.vorlaufTage} inputMode="numeric"
                hinweis="Mindestens 1. Fragen Sie im Zweifel Ihre Bank."
                onChange={e => setF({ ...f, vorlaufTage: e.target.value })} />
          <Feld label="Frist für fehlende Mandate (Tage)" value={f.mandatFristTage} inputMode="numeric"
                onChange={e => setF({ ...f, mandatFristTage: e.target.value })} />
          <div className="sm:col-span-2">
            <Knopf type="submit" variante="primaer" laedt={sendet}>Speichern</Knopf>
          </div>
        </form>
      )}
    </Karte>
  );
}

// ---------------------------------------------------------------------------

function EinzugsKarte({ einzug, monat, laeuft, tun, konfigBereit, setFehler }: {
  einzug: Einzug; monat: string; laeuft: string | null;
  tun: (n: string, fn: () => Promise<string>) => Promise<void>;
  konfigBereit: boolean; setFehler: (s: string | null) => void;
}) {
  const [faellig, setFaellig] = useState(einzug.fruehesteFaelligkeit);
  const [laedtDatei, setLaedtDatei] = useState(false);
  useEffect(() => { setFaellig(einzug.fruehesteFaelligkeit); }, [einzug.fruehesteFaelligkeit]);

  const offeneSumme = useMemo(() => euro(einzug.einziehbar.summe), [einzug.einziehbar.summe]);

  /**
   * Die Datei kommt als Download. Ein einfacher Link ginge nicht: sie liegt
   * hinter der Anmeldung, und ein <a href> schickt keinen Anmeldekopf mit.
   */
  async function dateiHolen() {
    setLaedtDatei(true);
    setFehler(null);
    try {
      const antwort = await fetch(apiUrl('/api/abrechnung/datei'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token() ?? ''}` },
        body: JSON.stringify({ monat, faelligAm: faellig }),
      });
      if (!antwort.ok) {
        const koerper = await antwort.json().catch(() => null);
        const maengel = koerper?.maengel as { posten: string | null; text: string }[] | undefined;
        throw new Error(maengel?.length
          ? `${koerper.error}: ${maengel.map(m => (m.posten ? `${m.posten} — ${m.text}` : m.text)).join(' · ')}`
          : koerper?.error || 'Die Datei ließ sich nicht erzeugen.');
      }
      const blob = await antwort.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lastschrift-${monat}-${faellig}.xml`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      globalThis.location.reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Datei ließ sich nicht erzeugen.');
    } finally { setLaedtDatei(false); }
  }

  return (
    <Karte>
      <KartenKopf
        titel="Einzug"
        hinweis={einzug.einziehbar.anzahl > 0
          ? `${einzug.einziehbar.anzahl} Posten über ${offeneSumme} bereit`
          : 'Nichts offen in diesem Monat'}
      />

      {einzug.einziehbar.anzahl > 0 && (
        <div className="space-y-3 border-b border-slate-200 p-5 dark:border-slate-800">
          {einzug.einziehbar.ohneAnkuendigung > 0 && (
            <Hinweis art="warnung">
              {einzug.einziehbar.ohneAnkuendigung} Posten sind noch nicht angekündigt. Die
              Vorabankündigung ist vor jedem Einzug Pflicht — kündigen Sie zuerst an.
            </Hinweis>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-48">
              <Feld label="Fälligkeitstag" type="date" value={faellig}
                    min={einzug.fruehesteFaelligkeit}
                    hinweis={`frühestens ${datum(einzug.fruehesteFaelligkeit)}`}
                    onChange={e => setFaellig(e.target.value)} />
            </div>
            <Knopf laedt={laeuft === 'ankuendigen'}
                   onClick={() => tun('ankuendigen', async () => {
                     const r = await api<{ angekuendigt: number; verschickt: number; ohneMailadresse: string[] }>(
                       '/abrechnung/ankuendigung', { methode: 'POST', daten: { monat, faelligAm: faellig } });
                     return `${r.angekuendigt} angekündigt, ${r.verschickt} Mails verschickt`
                       + (r.ohneMailadresse.length ? ` · ohne Adresse: ${r.ohneMailadresse.join(', ')}` : '');
                   })}>
              Vorab ankündigen
            </Knopf>
            <Knopf variante="primaer" laedt={laedtDatei} disabled={!konfigBereit} onClick={dateiHolen}>
              SEPA-Datei herunterladen
            </Knopf>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Die Datei laden Sie im Online-Banking Ihrer Bank als Sammel-Lastschrift hoch.
            Nach dem Herunterladen gelten die Posten als eingereicht.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr><th className="px-5 py-2">Mandant</th><th className="px-5 py-2">Rechnung</th>
              <th className="px-5 py-2">Konto</th><th className="px-5 py-2 text-right">Betrag</th>
              <th className="px-5 py-2">Einzug</th><th className="px-5 py-2">Ergebnis</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {einzug.zeilen.map(z => (
              <tr key={z.rechnungId}>
                <td className="px-5 py-2.5 font-medium text-slate-900 dark:text-slate-100">{z.mandantName}</td>
                <td className="px-5 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{z.nummer}</td>
                <td className="px-5 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                  {z.hatMandat
                    ? <>{z.kontoinhaber}<br /><span className="font-mono">{z.ibanKurz}</span></>
                    : <Abzeichen art="gelb">kein Mandat</Abzeichen>}
                </td>
                <td className="px-5 py-2.5 text-right font-medium">{euro(z.brutto)}</td>
                <td className="px-5 py-2.5">
                  {z.einzug
                    ? <Abzeichen art={EINZUG_FARBE[z.einzug.status]}>{EINZUG_NAME[z.einzug.status]}</Abzeichen>
                    : <span className="text-xs text-slate-400">—</span>}
                  {z.einzug?.faelligAm && (
                    <span className="ms-1.5 text-xs text-slate-400">{datum(z.einzug.faelligAm)}</span>
                  )}
                  {z.angekuendigtAm && <div className="text-[11px] text-slate-400">angekündigt</div>}
                </td>
                <td className="px-5 py-2.5">
                  {z.einzug?.status === 'eingereicht' && (
                    <div className="flex gap-1">
                      <Knopf className="!text-xs !px-2" laedt={laeuft === `ok${z.rechnungId}`}
                             onClick={() => tun(`ok${z.rechnungId}`, async () => {
                               await api(`/abrechnung/einzug/${z.rechnungId}/ergebnis`,
                                 { methode: 'POST', daten: { ergebnis: 'eingezogen' } });
                               return `${z.mandantName}: als eingezogen gebucht.`;
                             })}>eingezogen</Knopf>
                      <Knopf variante="gefahr" className="!text-xs !px-2" laedt={laeuft === `zu${z.rechnungId}`}
                             onClick={() => tun(`zu${z.rechnungId}`, async () => {
                               await api(`/abrechnung/einzug/${z.rechnungId}/ergebnis`,
                                 { methode: 'POST', daten: { ergebnis: 'zurueck', grund: 'Rücklastschrift' } });
                               return `${z.mandantName}: Rücklastschrift erfasst, Rechnung wieder offen.`;
                             })}>zurück</Knopf>
                    </div>
                  )}
                  {z.einzug?.grund && <span className="text-xs text-rose-600">{z.einzug.grund}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  );
}

// ---------------------------------------------------------------------------

function MandatsKarte({ mandate, laeuft, tun }: {
  mandate: { zeilen: MandatZeile[]; ohneMandat: number; gesperrt: number } | null;
  laeuft: string | null;
  tun: (n: string, fn: () => Promise<string>) => Promise<void>;
}) {
  if (!mandate) return null;
  return (
    <Karte>
      <KartenKopf
        titel="Lastschriftmandate"
        hinweis={`${mandate.ohneMandat} ohne Mandat · ${mandate.gesperrt} gesperrt`}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr><th className="px-5 py-2">Mandant</th><th className="px-5 py-2">Kontoinhaber</th>
              <th className="px-5 py-2">Referenz</th><th className="px-5 py-2">Stand</th>
              <th className="px-5 py-2"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {mandate.zeilen.map(z => (
              <tr key={z.tenantId}>
                <td className="px-5 py-2.5">
                  <span className="font-medium text-slate-900 dark:text-slate-100">{z.name}</span>
                  <span className="ms-2 text-xs text-slate-400">{z.plan}</span>
                </td>
                <td className="px-5 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                  {z.hatMandat ? <>{z.kontoinhaber}<br /><span className="font-mono">{z.ibanKurz}</span></> : '—'}
                </td>
                <td className="px-5 py-2.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                  {z.mandatsreferenz || '—'}
                </td>
                <td className="px-5 py-2.5">
                  {z.befreit ? <Abzeichen>{z.eigenerBetrieb ? 'eigener Betrieb' : 'kostenlos, kein Mandat nötig'}</Abzeichen>
                    : z.hatMandat ? (
                      <>
                        <Abzeichen art="gruen">seit {datum(z.unterschriebenAm)}</Abzeichen>
                        {z.vorEinrichtung && (
                          <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                            vor der Einrichtung erteilt — bitte erneut bestätigen lassen
                          </div>
                        )}
                      </>
                    )
                      : z.gesperrt ? <Abzeichen art="rot">gesperrt</Abzeichen>
                        : <Abzeichen art="gelb">noch {z.tageRest ?? '?'} Tage</Abzeichen>}
                </td>
                <td className="px-5 py-2.5 text-right">
                  {!z.hatMandat && !z.befreit && (
                    <Knopf className="!text-xs !px-2" laedt={laeuft === `frist${z.tenantId}`}
                           onClick={() => tun(`frist${z.tenantId}`, async () => {
                             await api(`/abrechnung/mandate/${z.tenantId}/frist`, { methode: 'POST', daten: { tage: 14 } });
                             return `${z.name}: Frist um 14 Tage verlängert.`;
                           })}>+14 Tage</Knopf>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  );
}
